import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLiveRefresh } from "../hooks/useLiveRefresh";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ROLE_LABELS, hasModuleAccess } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { LOCATION_LABELS, fmtNum } from "../lib/format";
import { Plus, Trash2, Upload, ChevronLeft, ChevronRight, CheckCircle2, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { OwnedBreakdown } from "../components/OwnedBreakdown";
import { ItemEditDialog } from "../components/ItemEditDialog";
import { RoomLinenParPanel } from "./RoomLinenPar";
import { useAuth, hasRole } from "../lib/auth";
import { ModuleAccessDialog } from "../components/ModuleAccessDialog";
import { VendorMaster } from "../components/VendorMaster";
import { LinenItemCreateForm } from "../components/LinenItemCreateForm";
import { TemplateMappingDialog } from "../components/TemplateMappingDialog";
import { MigrationReportsPanel } from "../components/MigrationReportsPanel";

export default function AdminSettings() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = hasRole(user, "admin");
  const canManageSettings = hasRole(user, "admin", "accounts", "hk_supervisor");
  const canAdjustLinenStock = hasRole(user, "admin", "accounts", "hk_supervisor")
    && hasModuleAccess(user, "master_linen_stock", "manage")
    && hasModuleAccess(user, "store_inventory", "manage");
  const [tab, setTab] = useState(isAdmin ? "users" : "hotel");
  const [settings, setSettings] = useState({});
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [assetVendors, setAssetVendors] = useState([]);
  const [openingStatus, setOpeningStatus] = useState({ finalized: false });
  const [masterRows, setMasterRows] = useState([]);
  const [pars, setPars] = useState({});
  const [selectedRT, setSelectedRT] = useState("");
  const [templateRows, setTemplateRows] = useState([]);
  const [mappingRow, setMappingRow] = useState(null);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [parDirty, setParDirty] = useState(false);
  const [parLoading, setParLoading] = useState(false);
  // Add Item to room-type template
  const [addTemplateOpen, setAddTemplateOpen] = useState(false);
  const [availableItems, setAvailableItems] = useState([]);
  const [addTemplatePick, setAddTemplatePick] = useState("");
  const [addTemplatePar, setAddTemplatePar] = useState("");
  const [addTemplateBusy, setAddTemplateBusy] = useState(false);
  // Remove template row with scope preview
  const [removeTemplateRow, setRemoveTemplateRow] = useState(null); // the row being removed
  const [removeTemplatePreview, setRemoveTemplatePreview] = useState(null);
  const [removeTemplateBusy, setRemoveTemplateBusy] = useState(false);
  // Linen item removal workflow (Items tab)
  const [itemsShowArchived, setItemsShowArchived] = useState(false);
  const [removeItem, setRemoveItem] = useState(null); // item object
  const [removeImpact, setRemoveImpact] = useState(null);
  const [removeItemBusy, setRemoveItemBusy] = useState(false);

  const [newUser, setNewUser] = useState({ email: "", name: "", role: "hk_staff", password: "" });
  const [editUser, setEditUser] = useState(null); // {id, email, name, role, active, password, must_change_password}
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [moduleAccessUser, setModuleAccessUser] = useState(null);
  const [newItem, setNewItem] = useState({ name: "", unit: "pcs", opening_qty: 0, low_stock_threshold: 0, par_min: 0 });
  const [editItem, setEditItem] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: "", contact: "" });
  const [assetItems, setAssetItems] = useState([]);
  const [assetCats, setAssetCats] = useState([]);
  const [assetDepts, setAssetDepts] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [deptEdits, setDeptEdits] = useState({});
  const [newRT, setNewRT] = useState({ name: "", description: "" });
  const [newRoom, setNewRoom] = useState({ number: "", room_type_id: "" });

  const [alloc, setAlloc] = useState([]);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState("");
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");

  // Bulk import state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("");
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMappingMode, setBulkMappingMode] = useState("");

  // Opening allocation wizard
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardRT, setWizardRT] = useState("");
  const [allocationRequestId, setAllocationRequestId] = useState("");
  const [allocationBusy, setAllocationBusy] = useState(false);

  const loadAll = async () => {
    const userRequest = isAdmin ? api.get("/users") : Promise.resolve({ data: [] });
    const [s, u, i, r, rt, v, os, m, pp, av] = await Promise.all([
      api.get("/settings"), userRequest, api.get("/items?include_inactive=true"), api.get("/rooms"),
      api.get("/room-types"), api.get("/vendors"), api.get("/opening-allocation/status"),
      api.get("/reports/master-stock"), api.get("/par-presets"), api.get("/assets/vendors"),
    ]);
    setSettings(s.data); setUsers(u.data); setItems(i.data); setRooms(r.data);
    setRoomTypes(rt.data); setVendors(v.data); setOpeningStatus(os.data); setMasterRows(m.data.rows);
    setPresets(Object.keys(pp.data.presets || {}));
    setAssetVendors(av.data);
    if (rt.data[0]) setSelectedRT(prev => prev || rt.data[0].id);
    // Asset Management items (read-only summary; managed in Asset Master)
    try {
      const [ai, ac, ad] = await Promise.all([api.get("/assets/items"), api.get("/assets/categories"), api.get("/assets/departments")]);
      setAssetItems(ai.data); setAssetCats(ac.data); setAssetDepts(ad.data);
    } catch (e) { console.warn("Asset module unavailable for this role:", e); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (canManageSettings) loadAll(); }, []);

  const addDept = async () => {
    if (!newDept.trim()) return toast.error("Department name required");
    try { await api.post("/assets/departments", { name: newDept.trim() }); toast.success("Department added"); setNewDept(""); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const renameDept = async (d) => {
    const name = (deptEdits[d.id] ?? d.name).trim();
    if (!name || name === d.name) return;
    try { await api.patch(`/assets/departments/${d.id}`, { name }); toast.success("Department renamed"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleDept = async (d) => {
    try { await api.patch(`/assets/departments/${d.id}`, { active: !d.active }); toast.success(d.active ? "Deactivated" : "Activated"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const loadTemplateRows = async () => {
    if (!selectedRT) {
      setTemplateRows([]);
      return;
    }
    const { data } = await api.get(`/room-types/${selectedRT}/linen-template`);
    setTemplateRows(data);
  };

  const refreshSelectedTemplate = useCallback(async () => {
    if (!selectedRT) {
      return;
    }

    setParLoading(true);
    try {
      const [parResponse, templateResponse] = await Promise.all([
        api.get(`/room-types/${selectedRT}/par`),
        api.get(`/room-types/${selectedRT}/linen-template`),
      ]);
      const nextPars = {};
      parResponse.data.forEach((par) => {
        nextPars[par.item_id] = par.par_qty;
      });
      setPars(nextPars);
      setTemplateRows(templateResponse.data);
    } finally {
      setParLoading(false);
    }
  }, [selectedRT]);

  useEffect(() => {
    if (!selectedRT) {
      return;
    }

    // Clear any stale par/template state immediately on room-type switch so
    // Save PAR cannot submit entries for the previous room type while the new
    // template is loading.
    setPars({});
    setTemplateRows([]);
    setParDirty(false);
    refreshSelectedTemplate().catch((error) => {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    });
  }, [refreshSelectedTemplate, selectedRT]);

  useLiveRefresh(
    refreshSelectedTemplate,
    tab === "par" && Boolean(selectedRT) && !parDirty,
  );

  const saveSettings = async () => {
    try { await api.patch("/settings", settings); toast.success("Saved"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addUser = async () => {
    try { await api.post("/users", newUser); toast.success("User created"); setNewUser({ email: "", name: "", role: "hk_staff", password: "" }); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleUser = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "Deactivated" : "Activated"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openEditUser = (u) => {
    setEditUser({
      id: u.id, email: u.email || "", name: u.name || "", role: u.role,
      active: u.active !== false, password: "", must_change_password: true,
    });
    setEditUserOpen(true);
  };
  const saveEditUser = async () => {
    if (!editUser) return;
    // Build payload with only the fields the admin actually wants to change
    const payload = {
      name: editUser.name,
      email: editUser.email,
      role: editUser.role,
      active: editUser.active,
      must_change_password: !!editUser.must_change_password,
    };
    if (editUser.password && editUser.password.length > 0) {
      if (editUser.password.length < 10) { toast.error("Password must be at least 10 characters"); return; }
      payload.password = editUser.password;
    }
    try {
      await api.patch(`/users/${editUser.id}`, payload);
      toast.success("User updated");
      setEditUserOpen(false);
      setEditUser(null);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This only succeeds if the user has no historical records. Otherwise use Deactivate.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addItem = async () => {
    try { await api.post("/items", { ...newItem, opening_qty: Number(newItem.opening_qty), low_stock_threshold: Number(newItem.low_stock_threshold), par_min: Number(newItem.par_min) }); toast.success("Item added"); setNewItem({ name: "", unit: "pcs", opening_qty: 0, low_stock_threshold: 0, par_min: 0 }); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const migrateRoomTemplates = async () => {
    setTemplateBusy(true);
    try {
      const { data } = await api.post("/room-type-templates/migrate");
      if (data.runner_par_conflicts?.length) {
        toast.error("Runner PAR conflicts need manual review before migration can finish.");
      } else {
        toast.success(`Template links ready. ${data.runner_par_migrated} Runner PAR link(s) updated.`);
      }
      await loadTemplateRows();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setTemplateBusy(false);
    }
  };
  const toggleItem = async (it) => {
    try {
      await api.patch(`/items/${it.id}`, { active: !it.active });
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const addVendor = async () => { try { await api.post("/vendors", newVendor); toast.success("Vendor added"); setNewVendor({ name: "", contact: "" }); loadAll(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const removeVendor = async (v) => {
    if (!window.confirm(`Remove vendor "${v.name}"? Vendors with dispatch history are marked Inactive to preserve records.`)) return;
    try { const { data } = await api.delete(`/vendors/${v.id}`); toast.success(data?.message || "Vendor removed"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addRT = async () => { try { await api.post("/room-types", newRT); toast.success("Room type added"); setNewRT({ name: "", description: "" }); loadAll(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const addRoom = async () => { try { await api.post("/rooms", newRoom); toast.success("Room added"); setNewRoom({ number: "", room_type_id: "" }); loadAll(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const savePars = async () => {
    // Submit only entries whose item_id is actually in the current template.
    // Prevents stale/orphan room_par rows (items present in room_par but no
    // longer in room_type_linen_templates) from being blindly re-sent — the
    // user can't see or edit those rows in this screen.
    const templateItemIds = new Set(
      (templateRows || [])
        .filter((r) => !r.orphaned && r.item_id)
        .map((r) => r.item_id)
    );
    const entries = Object.entries(pars)
      .filter(([item_id, v]) => {
        if (!templateItemIds.has(item_id)) return false;
        if (v === "" || v === null || v === undefined) return false;
        const n = Number(v);
        return !isNaN(n) && n >= 0 && Number.isInteger(n);
      })
      .map(([item_id, v]) => ({ item_id, par_qty: Number(v) }));
    try {
      await api.put(`/room-types/${selectedRT}/par`, { entries });
      await refreshSelectedTemplate();
      setParDirty(false);
      toast.success("Par saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const openAddTemplate = async () => {
    if (!selectedRT) return;
    setAddTemplatePick("");
    setAddTemplatePar("");
    setAddTemplateOpen(true);
    try {
      const { data } = await api.get(`/room-types/${selectedRT}/available-items`);
      setAvailableItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load items");
      setAvailableItems([]);
    }
  };

  const submitAddTemplate = async () => {
    if (!selectedRT || !addTemplatePick) return;
    const body = { item_id: addTemplatePick };
    if (addTemplatePar !== "") {
      const n = Number(addTemplatePar);
      if (!Number.isInteger(n) || n < 0) { toast.error("PAR must be a whole number >= 0"); return; }
      body.par_qty = n;
    }
    setAddTemplateBusy(true);
    try {
      const { data } = await api.post(`/room-types/${selectedRT}/linen-template`, body);
      toast.success(`Added ${data.item_name}${data.par_set ? ` (PAR ${body.par_qty})` : ""}`);
      setAddTemplateOpen(false);
      await refreshSelectedTemplate();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Add failed");
    } finally {
      setAddTemplateBusy(false);
    }
  };

  const openRemoveTemplate = async (row) => {
    setRemoveTemplateRow(row);
    setRemoveTemplatePreview(null);
    try {
      const { data } = await api.get(
        `/room-types/${selectedRT}/linen-template/${encodeURIComponent(row.id)}/delete-preview`
      );
      setRemoveTemplatePreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load preview");
    }
  };

  const confirmRemoveTemplate = async () => {
    if (!removeTemplateRow || !selectedRT) return;
    setRemoveTemplateBusy(true);
    try {
      const { data } = await api.delete(
        `/room-types/${selectedRT}/linen-template/${encodeURIComponent(removeTemplateRow.id)}`
      );
      toast.success(
        `Removed${data.item_name ? ` ${data.item_name}` : ""} from this room-type template` +
          (data.par_preserved_due_to_duplicate ? " (duplicate retained, PAR preserved)" : "")
      );
      setRemoveTemplateRow(null);
      setRemoveTemplatePreview(null);
      await refreshSelectedTemplate();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Remove failed");
    } finally {
      setRemoveTemplateBusy(false);
    }
  };

  const openRemoveItem = async (item) => {
    setRemoveItem(item);
    setRemoveImpact(null);
    try {
      const { data } = await api.get(`/items/${item.id}/removal-impact`);
      setRemoveImpact(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load impact");
    }
  };

  const confirmRemoveItem = async (hardDelete = false) => {
    if (!removeItem) return;
    setRemoveItemBusy(true);
    try {
      const { data } = await api.post(`/items/${removeItem.id}/remove`, {
        cascade: true,
        hard_delete_if_safe: !!hardDelete,
        reason: "Admin cleanup — Admin Settings UI",
      });
      toast.success(
        data.action === "hard_deleted"
          ? `${removeItem.name} permanently deleted`
          : `${removeItem.name} archived (template rows: ${data.cascade_removed.template_mappings}, par defaults: ${data.cascade_removed.room_par_defaults}, overrides: ${data.cascade_removed.room_overrides} removed)`
      );
      setRemoveItem(null);
      setRemoveImpact(null);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Remove failed");
    } finally {
      setRemoveItemBusy(false);
    }
  };

  const restoreItem = async (item) => {
    try {
      await api.post(`/items/${item.id}/restore`);
      toast.success(`${item.name} restored (re-link templates via Room Linen Templates > Add Item)`);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    }
  };

  const clonePars = async () => {
    if (!cloneSourceId || cloneSourceId === selectedRT) { toast.error("Pick a different source room type"); return; }
    try {
      const { data } = await api.post(`/room-types/${cloneSourceId}/clone-par-to/${selectedRT}`);
      toast.success(`Copied ${data.count} par values from ${data.source_name} to ${data.target_name}`);
      setCloneOpen(false);
      // Reload par for target
      api.get(`/room-types/${selectedRT}/par`).then(({ data }) => {
        const m = {}; data.forEach(p => { m[p.item_id] = p.par_qty; }); setPars(m);
      });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const applyPreset = async (name) => {
    if (!name || !selectedRT) return;
    if (!window.confirm(`Apply the "${name}" preset — this will REPLACE current par values for this room type. Continue?`)) return;
    try {
      const { data } = await api.post(`/room-types/${selectedRT}/apply-preset/${name}`);
      const msg = `Applied ${data.preset}: ${data.count} par values set` + (data.skipped_items?.length ? ` (${data.skipped_items.length} items not in your catalog were skipped)` : "");
      toast.success(msg);
      api.get(`/room-types/${selectedRT}/par`).then(({ data }) => {
        const m = {}; data.forEach(p => { m[p.item_id] = p.par_qty; }); setPars(m);
      });
      setPresetName("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addAllocLine = () => setAlloc([...alloc, { item_id: "", location: "ROOM_CLEAN", room_id: "", vendor_id: "", qty: 0 }]);
  const submitAlloc = async (finalize = false) => {
    const clean = alloc.filter(l => l.item_id && Number(l.qty) > 0);
    const requestId = openingStatus.baseline_active
      ? allocationRequestId || globalThis.crypto?.randomUUID?.()
      : undefined;
    if (openingStatus.baseline_active && !requestId) {
      toast.error("This browser cannot create a safe allocation request id.");
      return;
    }
    setAllocationRequestId(requestId || "");
    setAllocationBusy(true);
    try {
      await api.post("/opening-allocation", {
        lines: clean.map(l => ({ ...l, qty: Number(l.qty) })),
        finalize,
        client_request_id: requestId,
      });
      toast.success(finalize ? "Opening allocation finalized" : "Allocation recorded");
      setAlloc([]);
      setWizardStep(1);
      setAllocationRequestId("");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAllocationBusy(false);
    }
  };

  // ---- Bulk Item Import (CSV) ----
  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    // Header detection
    const first = lines[0].toLowerCase();
    const hasHeader = first.includes("name") && (first.includes("category") || first.includes("unit") || first.includes(","));
    const bodyLines = hasHeader ? lines.slice(1) : lines;
    const header = hasHeader ? lines[0].split(",").map(s => s.trim().toLowerCase()) :
      ["name", "unit", "opening_qty", "low_stock_threshold", "par_min"];
    const idx = (k) => header.indexOf(k);
    return bodyLines.map((row, i) => {
      const cols = row.split(",").map(s => s.trim());
      const g = (k, def = "") => (idx(k) >= 0 ? (cols[idx(k)] ?? "") : "") || def;
      return {
        _row: i + (hasHeader ? 2 : 1),
        name: g("name"),
        unit: g("unit", "pcs"),
        opening_qty: Number(g("opening_qty", "0")) || 0,
        low_stock_threshold: Number(g("low_stock_threshold", "0")) || 0,
        par_min: Number(g("par_min", "0")) || 0,
        mapping_mode: bulkMappingMode,
        room_pars: [],
      };
    }).filter(r => r.name);
  };

  const previewCsv = () => {
    if (!bulkMappingMode) {
      toast.error("Choose the linen use before importing");
      return;
    }
    const rows = parseCsv(bulkCsv);
    setBulkPreview(rows);
    if (!rows.length) toast.error("No rows parsed. Make sure at least a 'name' column has values.");
    else toast.success(`Parsed ${rows.length} rows. Review below and click Import to save.`);
  };

  const importCsv = async () => {
    if (!bulkPreview.length) { toast.error("Nothing to import — parse the CSV first."); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/items/bulk-import", { items: bulkPreview.map(({ _row, ...rest }) => rest) });
      const c = data.counts;
      toast.success(`Imported ${c.created}/${c.requested} items` + (c.skipped ? ` (${c.skipped} skipped)` : ""));
      if (data.skipped?.length) {
        console.warn("Bulk import skipped:", data.skipped);
      }
      setBulkOpen(false); setBulkCsv(""); setBulkPreview([]); loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setBulkBusy(false); }
  };

  // ---- Opening Allocation Wizard: auto-suggest lines from par values ----
  const autoSuggestAllocation = async () => {
    if (!wizardRT) { toast.error("Pick a room type first — the wizard uses its par values."); return; }
    try {
      const { data: pars } = await api.get(`/room-types/${wizardRT}/par`);
      if (!pars.length) { toast.error("This room type has no par values yet. Add pars first, then re-run the wizard."); return; }
      // Match rooms of this type
      const targetRooms = rooms.filter(r => r.room_type_id === wizardRT && r.active !== false);
      if (!targetRooms.length) { toast.error("No active rooms found for this room type."); return; }
      const suggested = [];
      for (const p of pars) {
        if (!p.par_qty || p.par_qty <= 0) continue;
        const it = items.find(x => x.id === p.item_id);
        const storeQty = masterRows.find(r => r.item_id === p.item_id)?.STORE_CLEAN || 0;
        if (!storeQty) continue;
        // Suggest par_qty per room, capped by store availability
        let remaining = storeQty;
        for (const room of targetRooms) {
          const perRoom = Math.min(p.par_qty, remaining);
          if (perRoom <= 0) break;
          suggested.push({ item_id: p.item_id, location: "ROOM_CLEAN", room_id: room.id, vendor_id: "", qty: perRoom, item_name: it?.name, room_number: room.number });
          remaining -= perRoom;
        }
      }
      if (!suggested.length) { toast.error("No lines could be suggested — items have no store stock yet."); return; }
      setAlloc(suggested);
      setWizardStep(2);
      toast.success(`Suggested ${suggested.length} allocation lines from par × ${targetRooms.length} rooms.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const wizardTotalQty = alloc.reduce((s, l) => s + (Number(l.qty) || 0), 0);
  const wizardLinesValid = alloc.filter(l => l.item_id && Number(l.qty) > 0).length;

  return (
    <Layout>
      <h1 className="section-title" data-testid="page-title">{t("nav.admin")}</h1>
      <div className="section-sub mb-4">{t("sub.admin")}</div>

      <Tabs value={tab} onValueChange={setTab}>
        {canManageSettings && <TabsList className="mb-3 flex flex-wrap h-auto">
          <TabsTrigger value="hotel" data-testid="tab-hotel">Hotel</TabsTrigger>
          <TabsTrigger value="items" data-testid="tab-items">Items</TabsTrigger>
          <TabsTrigger value="rooms" data-testid="tab-rooms">Rooms &amp; Types</TabsTrigger>
          <TabsTrigger value="par" data-testid="tab-par">Room Linen Templates</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
          <TabsTrigger value="roompar" data-testid="tab-roompar">Room Linen Par</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors</TabsTrigger>
          <TabsTrigger value="opening" data-testid="tab-opening">Opening Allocation</TabsTrigger>
          {isAdmin && <TabsTrigger value="migration-reports" data-testid="tab-migration-reports">Migration Reports</TabsTrigger>}
        </TabsList>}

        <TabsContent value="hotel">
          <Card className="p-6 space-y-3 max-w-2xl">
            <div><Label>Hotel Name</Label><Input value={settings.hotel_name || ""} onChange={e => setSettings({ ...settings, hotel_name: e.target.value })} className="h-11 mt-1" /></div>
            <div><Label>Timezone</Label><Input value={settings.timezone || ""} onChange={e => setSettings({ ...settings, timezone: e.target.value })} className="h-11 mt-1" data-testid="settings-timezone" /></div>
            <div><Label>Daily Opening Time</Label><Input type="time" value={settings.opening_time || ""} onChange={e => setSettings({ ...settings, opening_time: e.target.value })} className="h-11 mt-1" data-testid="settings-opening-time" /></div>
            <div><Label>Daily Closing Time</Label><Input type="time" value={settings.closing_time || ""} onChange={e => setSettings({ ...settings, closing_time: e.target.value })} className="h-11 mt-1" /></div>
            <div><Label>Logo URL</Label><Input value={settings.logo_url || ""} onChange={e => setSettings({ ...settings, logo_url: e.target.value })} className="h-11 mt-1" /></div>
            <Button onClick={saveSettings} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-settings">Save</Button>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card className="p-4 mb-3">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div><Label>Email</Label><Input value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} className="h-11 mt-1" data-testid="new-user-email" /></div>
              <div><Label>Name</Label><Input value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} className="h-11 mt-1" data-testid="new-user-name" /></div>
              <div><Label>Role</Label><Select value={newUser.role} onValueChange={v => setNewUser({ ...newUser, role: v })}><SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div>
                <Label>Temporary password</Label>
                <Input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} className="h-11 mt-1" data-testid="new-user-password" placeholder="min 10 characters" />
                <div className="text-[11px] text-slate-500 mt-1">User will be forced to change on first login.</div>
              </div>
              <div><Button onClick={addUser} className="h-11 bg-slate-900 text-white hover:bg-slate-800" data-testid="add-user">Add User</Button></div>
            </div>
          </Card>
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto"><table className="vr-table">
              <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Status</th><th className="no-print">Actions</th></tr></thead>
              <tbody>{users.map(u => (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td>{u.email}</td>
                  <td>{u.name}</td>
                  <td>{ROLE_LABELS[u.role] || u.role}</td>
                  <td className="whitespace-nowrap">
                    {u.active !== false
                      ? <Badge className="bg-emerald-100 border text-emerald-800">Active</Badge>
                      : <Badge className="bg-slate-100 border text-slate-700">Inactive</Badge>}
                    {u.must_change_password && (
                      <Badge className="ml-2 bg-amber-100 border text-amber-800" title="User will be forced to change password at next login">
                        Must change pwd
                      </Badge>
                    )}
                    {u.locked_until && (
                      <Badge className="ml-2 bg-red-100 border text-red-800" data-testid={`locked-user-${u.email}`}>
                        Locked
                      </Badge>
                    )}
                  </td>
                  <td className="no-print whitespace-nowrap">
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.email}`}>Edit</Button>
                      {u.role !== "admin" && <Button size="sm" variant="outline" onClick={() => setModuleAccessUser(u)} data-testid={`module-access-user-${u.email}`}>Module Access</Button>}
                      <Button size="sm" variant="outline" onClick={() => toggleUser(u)} data-testid={`toggle-user-${u.email}`}>
                        {u.active !== false ? "Deactivate" : "Activate"}
                      </Button>
                      {u.locked_until && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            await api.post(`/users/${u.id}/unlock-and-sign-out`);
                            const response = await api.get("/users");
                            setUsers(response.data);
                            toast.success("Account unlocked and sessions revoked");
                          }}
                          data-testid={`unlock-user-${u.email}`}
                        >
                          Unlock account and sign out all sessions
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.email}`} className="text-red-700 hover:bg-red-50">
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          </Card>

          {/* Edit User dialog */}
          <Dialog open={editUserOpen} onOpenChange={(v) => { setEditUserOpen(v); if (!v) setEditUser(null); }}>
            <DialogContent className="max-w-lg" data-testid="edit-user-dialog">
              <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
              {editUser && (
                <div className="space-y-3">
                  <div>
                    <Label>Email</Label>
                    <Input value={editUser.email} onChange={e => setEditUser({ ...editUser, email: e.target.value })} className="h-11 mt-1" data-testid="edit-user-email" />
                    <div className="text-[11px] text-slate-500 mt-1">Changing the email signs the user out of any active session.</div>
                  </div>
                  <div>
                    <Label>Name</Label>
                    <Input value={editUser.name} onChange={e => setEditUser({ ...editUser, name: e.target.value })} className="h-11 mt-1" data-testid="edit-user-name" />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select value={editUser.role} onValueChange={v => setEditUser({ ...editUser, role: v })}>
                      <SelectTrigger className="h-11 mt-1" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id="edit-user-active"
                      type="checkbox"
                      checked={!!editUser.active}
                      onChange={e => setEditUser({ ...editUser, active: e.target.checked })}
                      data-testid="edit-user-active"
                    />
                    <Label htmlFor="edit-user-active" className="!mb-0">Account active</Label>
                  </div>
                  <div>
                    <Label>Reset password (optional)</Label>
                    <Input type="password" value={editUser.password}
                      onChange={e => setEditUser({ ...editUser, password: e.target.value })}
                      className="h-11 mt-1" placeholder="Leave blank to keep current password"
                      data-testid="edit-user-password" />
                    <div className="text-[11px] text-slate-500 mt-1">If set, must be at least 10 characters. Sessions will be invalidated.</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id="edit-user-mcp"
                      type="checkbox"
                      checked={!!editUser.must_change_password}
                      onChange={e => setEditUser({ ...editUser, must_change_password: e.target.checked })}
                      data-testid="edit-user-mcp"
                    />
                    <Label htmlFor="edit-user-mcp" className="!mb-0">Force password change on next login</Label>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditUserOpen(false)} data-testid="edit-user-cancel">Cancel</Button>
                <Button onClick={saveEditUser} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="edit-user-save">Save changes</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <ModuleAccessDialog
            open={!!moduleAccessUser}
            onOpenChange={open => !open && setModuleAccessUser(null)}
            target={moduleAccessUser}
            onSaved={loadAll}
          />
        </TabsContent>

        <TabsContent value="items">
          <Card className="p-4 mb-3">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-slate-700">Add a single item</div>
              <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="open-bulk-import" className="gap-2">
                <Upload className="w-4 h-4" /> Bulk import (CSV)
              </Button>
            </div>
            <LinenItemCreateForm roomTypes={roomTypes} onCreated={loadAll} />
          </Card>
          <Card className="p-0 overflow-hidden">
            <div className="flex items-center justify-between px-4 pt-4">
              <div className="text-sm font-semibold text-slate-700">Linen Items Master</div>
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="items-show-archived-toggle">
                <input type="checkbox" checked={itemsShowArchived} onChange={e => setItemsShowArchived(e.target.checked)} className="h-4 w-4" />
                Show archived
              </label>
            </div>
            <div className="overflow-x-auto mt-3"><table className="vr-table">
              <thead><tr><th>Name</th><th>Unit</th><th className="num">Opening</th><th className="num">Low</th><th className="num">Owned Total</th><th>Status</th><th className="no-print">Actions</th></tr></thead>
              <tbody>{items
                .filter(it => itemsShowArchived ? true : !(it.archived || it.active === false))
                .map(it => {
                  const isArchived = it.archived || it.active === false;
                  return (
                    <tr key={it.id} className={isArchived ? "opacity-70" : ""}>
                      <td>{it.name}</td>
                      <td>{it.unit}</td>
                      <td className="num">{fmtNum(it.opening_qty)}</td>
                      <td className="num">{fmtNum(it.low_stock_threshold)}</td>
                      <td className="num"><OwnedBreakdown total={it.owned_total || 0} rows={Object.entries(it.breakdown || {}).map(([label, qty]) => ({ label, qty }))} testid={`owned-total-${it.name}`} /></td>
                      <td>{it.archived ? <Badge className="bg-amber-100 border text-amber-800">Archived</Badge> : it.active !== false ? <Badge className="bg-emerald-100 border text-emerald-800">Active</Badge> : <Badge className="bg-slate-100 border text-slate-700">Inactive</Badge>}</td>
                      <td className="no-print">
                        <div className="flex items-center gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => { setEditItem(it); setEditOpen(true); }} data-testid={`edit-item-${it.name}`}>Edit</Button>
                          {isArchived ? (
                            <Button size="sm" variant="outline" onClick={() => restoreItem(it)} data-testid={`restore-item-${it.id}`}>Restore</Button>
                          ) : (
                            <Button size="icon" variant="ghost" onClick={() => openRemoveItem(it)} data-testid={`remove-item-${it.id}`} aria-label={`Remove ${it.name}`}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {items.filter(it => itemsShowArchived ? true : !(it.archived || it.active === false)).length === 0 && (
                  <tr><td colSpan={7} className="text-center text-slate-500 py-4">No items.</td></tr>
                )}
              </tbody>
            </table></div>
          </Card>
          <ItemEditDialog
            open={editOpen} onOpenChange={setEditOpen} item={editItem} apiBase="/items"
            title="Edit Linen Item"
            fields={[{ key: "name", label: "Name" }, { key: "unit", label: "Unit" }, { key: "low_stock_threshold", label: "Low Stock Threshold", type: "number" }]}
            stockSummary={masterRows.find(row => row.item_id === editItem?.id)}
            adjustmentHref={canAdjustLinenStock && editItem ? `/store?adjustItem=${editItem.id}` : null}
            onSaved={loadAll}
          />

          <Dialog open={Boolean(removeItem)} onOpenChange={open => { if (!open) { setRemoveItem(null); setRemoveImpact(null); } }}>
            <DialogContent className="max-w-2xl" data-testid="remove-item-dialog">
              <DialogHeader><DialogTitle>Remove {removeItem?.name || "linen item"} from Admin</DialogTitle></DialogHeader>
              {!removeImpact ? (
                <div className="text-sm text-slate-600 py-3">Loading impact…</div>
              ) : (
                <div className="space-y-3 text-sm max-h-[65vh] overflow-y-auto pr-1">
                  <div className="bg-slate-50 border border-slate-200 rounded p-3">
                    <div className="font-semibold text-slate-900">{removeImpact.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Current stock: <span className={removeImpact.stock_total > 0 ? "font-bold text-amber-800" : "font-semibold"}>{removeImpact.stock_total}</span>
                      {removeImpact.stock_by_location.length > 0 && (
                        <ul className="list-disc list-inside mt-1 text-slate-700">
                          {removeImpact.stock_by_location.map((l) => (
                            <li key={l.location_key}>{l.location_label}: <span className="font-mono">{l.qty}</span></li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {removeImpact.possible_duplicates.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded p-3">
                      <div className="font-semibold text-amber-900">Possible duplicate of an existing item</div>
                      <ul className="list-disc list-inside mt-1 text-amber-900">
                        {removeImpact.possible_duplicates.map(d => (
                          <li key={d.id}><span className="font-semibold">{d.name}</span>{d.size ? ` · ${d.size}` : ""} — owned total {d.owned_total}</li>
                        ))}
                      </ul>
                      {removeImpact.stock_total > 0 && (
                        <div className="mt-2 text-xs text-amber-900">
                          This item carries stock. We will not silently discard or transfer physical inventory. Reconcile the stock first (transfer units into the retained item above via the stock adjustment flow, or write off through missing/damage), then remove.
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <div className="font-semibold text-slate-800 mb-1">Will be removed with this action (cascade)</div>
                    <ul className="list-disc list-inside text-slate-700 space-y-1">
                      <li>{removeImpact.template_mappings_count} room-type template mapping(s){removeImpact.template_mappings_detail.length ? ` — ${removeImpact.template_mappings_detail.map(m => m.room_type_name || m.room_type_id).join(", ")}` : ""}</li>
                      <li>{removeImpact.room_par_defaults_count} room-type default PAR row(s){removeImpact.room_par_defaults_detail.length ? ` — ${removeImpact.room_par_defaults_detail.map(m => `${m.room_type_name || m.room_type_id} (PAR ${m.par_qty})`).join(", ")}` : ""}</li>
                      <li>{removeImpact.room_overrides_count} per-room PAR override(s){removeImpact.room_overrides_detail.length ? ` — rooms ${removeImpact.room_overrides_detail.map(m => `${m.room_number ?? m.room_id} (PAR ${m.par_qty})`).join(", ")}` : ""}</li>
                      <li>Item will be marked <span className="font-semibold">Archived</span> and hidden from the default Items list and all new-entry selectors.</li>
                    </ul>
                  </div>

                  <div>
                    <div className="font-semibold text-emerald-800 mb-1">Preserved (not touched)</div>
                    <ul className="list-disc list-inside text-emerald-800 space-y-1">
                      <li>Historical identity (master row kept so old reports stay readable)</li>
                      <li>Ledger: {removeImpact.historical_refs.ledger} row(s)</li>
                      <li>Housekeeping entries: {removeImpact.historical_refs.hk_entries}</li>
                      <li>Purchases: {removeImpact.historical_refs.purchases} · Dispatches: {removeImpact.historical_refs.dispatches} · Receipts: {removeImpact.historical_refs.receipts}</li>
                      <li>Closings: {removeImpact.historical_refs.closings} · Missing: {removeImpact.historical_refs.missing} · Damaged: {removeImpact.historical_refs.damaged}</li>
                    </ul>
                  </div>

                  {removeImpact.can_hard_delete && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-emerald-900 text-xs">
                      This item has <span className="font-semibold">no stock and no historical references</span>, so it can be permanently deleted instead of archived.
                    </div>
                  )}
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => { setRemoveItem(null); setRemoveImpact(null); }} disabled={removeItemBusy}>Cancel</Button>
                {removeImpact?.can_hard_delete && (
                  <Button
                    variant="outline"
                    onClick={() => confirmRemoveItem(true)}
                    disabled={removeItemBusy}
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    data-testid="remove-item-hard-delete"
                  >
                    {removeItemBusy ? "Deleting…" : "Permanently delete"}
                  </Button>
                )}
                <Button
                  onClick={() => confirmRemoveItem(false)}
                  disabled={!removeImpact || removeItemBusy || (removeImpact?.stock_total || 0) > 0}
                  className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                  data-testid="remove-item-archive"
                  title={removeImpact?.stock_total > 0 ? "Reconcile stock first" : ""}
                >
                  {removeItemBusy ? "Removing…" : "Archive & remove mappings"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Card className="p-0 overflow-hidden mt-4" data-testid="admin-asset-items">
            <div className="flex items-center justify-between px-4 pt-4">
              <div className="text-sm font-semibold text-slate-700">Asset Management Items <Badge variant="outline" className="ml-1 text-[10px]">separate from linen stock</Badge></div>
              <Button size="sm" variant="outline" onClick={() => window.location.assign("/assets/master")} data-testid="goto-asset-master">Manage in Asset Master</Button>
            </div>
            <div className="overflow-x-auto mt-3"><table className="vr-table">
              <thead><tr><th>SKU</th><th>Name</th><th>Category</th><th>Tracking</th><th>Unit</th><th className="num">Min</th><th>Status</th></tr></thead>
              <tbody>{assetItems.map(ai => (
                <tr key={ai.id}>
                  <td className="font-mono text-xs">{ai.sku}</td><td>{ai.name}</td>
                  <td className="text-xs">{assetCats.find(c => c.id === ai.category_id)?.name || "—"}</td>
                  <td className="text-xs capitalize">{ai.tracking_mode}</td><td>{ai.unit}</td>
                  <td className="num">{ai.min_level || 0}</td>
                  <td>{ai.active ? <Badge className="bg-emerald-100 border text-emerald-800">Active</Badge> : <Badge className="bg-slate-100 border text-slate-700">Inactive</Badge>}</td>
                </tr>
              ))}</tbody>
            </table></div>
            {assetItems.length === 0 && <div className="p-4 text-sm text-slate-500">No asset items found.</div>}
          </Card>

          <Card className="p-4 mt-4" data-testid="admin-asset-departments">
            <div className="text-sm font-semibold text-slate-700 mb-1">Asset Departments</div>
            <div className="text-xs text-slate-500 mb-3">Active departments appear in the Item Master department dropdown. Departments cannot be deleted (they may be referenced by items); deactivate instead.</div>
            <div className="flex gap-2 mb-4 max-w-md">
              <Input value={newDept} onChange={e => setNewDept(e.target.value)} placeholder="New department name" className="h-10" data-testid="new-dept-name" />
              <Button onClick={addDept} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="add-dept">Add</Button>
            </div>
            <div className="overflow-x-auto"><table className="vr-table" data-testid="departments-table">
              <thead><tr><th>Department</th><th>Status</th><th className="no-print">Actions</th></tr></thead>
              <tbody>
                {assetDepts.map(d => (
                  <tr key={d.id}>
                    <td>
                      <Input value={deptEdits[d.id] ?? d.name} onChange={e => setDeptEdits({ ...deptEdits, [d.id]: e.target.value })}
                        onBlur={() => renameDept(d)} className="h-9 max-w-xs" data-testid={`dept-name-${d.id}`} />
                    </td>
                    <td>{d.active ? <Badge className="bg-emerald-100 border text-emerald-800">Active</Badge> : <Badge className="bg-slate-100 border text-slate-700">Inactive</Badge>}</td>
                    <td className="no-print"><Button size="sm" variant="outline" onClick={() => toggleDept(d)} data-testid={`toggle-dept-${d.id}`}>{d.active ? "Deactivate" : "Activate"}</Button></td>
                  </tr>
                ))}
                {assetDepts.length === 0 && <tr><td colSpan={3} className="text-center text-slate-500 py-4">No departments.</td></tr>}
              </tbody>
            </table></div>
          </Card>
        </TabsContent>

        <TabsContent value="rooms">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            <Card className="p-4">
              <div className="font-semibold mb-2">Room Types</div>
              <div className="flex gap-2 items-end mb-3">
                <div className="flex-1"><Label>Name</Label><Input value={newRT.name} onChange={e => setNewRT({ ...newRT, name: e.target.value })} className="h-11 mt-1" /></div>
                <Button onClick={addRT} className="h-11 bg-slate-900 text-white hover:bg-slate-800">Add</Button>
              </div>
              <div className="space-y-1">{roomTypes.map(rt => <div key={rt.id} className="text-sm text-slate-700 border-b border-slate-100 py-1">{rt.name}</div>)}</div>
            </Card>
            <Card className="p-4">
              <div className="font-semibold mb-2">Rooms</div>
              <div className="grid grid-cols-3 gap-2 items-end mb-3">
                <div><Label>Number</Label><Input value={newRoom.number} onChange={e => setNewRoom({ ...newRoom, number: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>Type</Label><Select value={newRoom.room_type_id} onValueChange={v => setNewRoom({ ...newRoom, room_type_id: v })}><SelectTrigger className="h-11 mt-1"><SelectValue placeholder="Type" /></SelectTrigger><SelectContent>{roomTypes.map(rt => <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>)}</SelectContent></Select></div>
                <Button onClick={addRoom} className="h-11 bg-slate-900 text-white hover:bg-slate-800">Add</Button>
              </div>
              <div className="text-xs text-slate-500">Total rooms: {rooms.length}</div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="par">
          <Card className="p-4 mb-3">
            <div className="flex items-end gap-3 flex-wrap">
              <div><Label>Room Type</Label><Select value={selectedRT} onValueChange={setSelectedRT}><SelectTrigger className="h-11 mt-1 min-w-[220px]" data-testid="par-roomtype-select"><SelectValue placeholder="Pick" /></SelectTrigger><SelectContent>{roomTypes.map(rt => <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Load Preset</Label>
                <Select value={presetName} onValueChange={(v) => { setPresetName(v); applyPreset(v); }}>
                  <SelectTrigger className="h-11 mt-1 min-w-[200px]" data-testid="preset-select"><SelectValue placeholder="Pick a preset" /></SelectTrigger>
                  <SelectContent>{presets.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={savePars} disabled={parLoading || !selectedRT} className="h-11 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60" data-testid="save-par">{parLoading ? "Loading…" : "Save Par"}</Button>
              <Button onClick={openAddTemplate} disabled={parLoading || !selectedRT} variant="outline" className="h-11" data-testid="add-template-item-btn"><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
              <Button onClick={() => setCloneOpen(true)} variant="outline" className="h-11" data-testid="clone-par-btn" disabled={roomTypes.length < 2}>Clone from another type</Button>
              <Button
                onClick={migrateRoomTemplates}
                variant="outline"
                className="h-11"
                disabled={templateBusy}
                data-testid="migrate-template-links"
              >
                Repair legacy mappings
              </Button>
            </div>
            <div className="mt-2 text-xs text-slate-500">Presets replace current par values for the selected type. "Save Par" writes your on-screen edits.</div>
            <div className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2">
              <span className="font-semibold">These are room-type defaults.</span> They apply to every active room of the selected type. Per-room PAR overrides are managed in the <span className="font-semibold">Room Linen Par</span> tab and are <span className="font-semibold">preserved automatically</span> when you add, edit, or remove items here.
            </div>
          </Card>
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto"><table className="vr-table">
              <thead><tr><th>Linen Item (from room-type template)</th><th className="num">Default PAR (per room)</th><th className="w-16"></th></tr></thead>
              <tbody>{(() => {
                if (!selectedRT) return <tr><td colSpan={3} className="text-center text-slate-500 py-4">Pick a room type to configure its linen template.</td></tr>;
                if (!templateRows.length) return <tr><td colSpan={3} className="text-center text-slate-500 py-4">No items in this template yet. Click <span className="font-semibold">Add Item</span> above to link an existing master linen item.</td></tr>;
                return templateRows.map(row => {
                  if (row.orphaned) return <tr key={row.id} className="opacity-70"><td>{row.legacy_label} <Button variant="link" size="sm" onClick={() => setMappingRow(row)} data-testid={`resolve-template-${row.id}`}>Resolve Mapping</Button></td><td className="num text-right text-slate-400">—</td><td><Button variant="ghost" size="icon" onClick={() => openRemoveTemplate(row)} data-testid={`template-remove-${row.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button></td></tr>;
                  return (
                    <tr key={row.id}>
                      <td>{row.item_name}</td>
                      <td className="num">
                        <Input
                          type="number"
                          min="0"
                          placeholder="Not Configured"
                          value={pars[row.item_id] ?? ""}
                          onChange={(e) => {
                            setParDirty(true);
                            setPars({ ...pars, [row.item_id]: e.target.value });
                          }}
                          className="h-9 w-36 ml-auto"
                          data-testid={`par-${row.item_id}`}
                        />
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openRemoveTemplate(row)}
                          data-testid={`template-remove-${row.id}`}
                          aria-label={`Remove ${row.item_name}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </td>
                    </tr>
                  );
                });
              })()}</tbody>
            </table></div>
          </Card>
          <TemplateMappingDialog
            open={Boolean(mappingRow)}
            row={mappingRow}
            roomTypeId={selectedRT}
            items={items}
            onOpenChange={open => !open && setMappingRow(null)}
            onResolved={loadTemplateRows}
          />

          {/* Add Item dialog */}
          <Dialog open={addTemplateOpen} onOpenChange={setAddTemplateOpen}>
            <DialogContent data-testid="add-template-dialog">
              <DialogHeader><DialogTitle>Add linen item to this room-type template</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-slate-600">
                  Only items already in <span className="font-semibold">Linen Items Master</span> and not yet in this template appear below. Adding will not create a new master item or duplicate template row.
                </div>
                <div>
                  <Label>Linen item</Label>
                  <Select value={addTemplatePick} onValueChange={setAddTemplatePick}>
                    <SelectTrigger className="h-11 mt-1" data-testid="add-template-item-select"><SelectValue placeholder={availableItems.length ? "Pick an item" : "No eligible items"} /></SelectTrigger>
                    <SelectContent>
                      {availableItems.map(it => (
                        <SelectItem key={it.id} value={it.id}>{it.name}{it.size ? ` · ${it.size}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Default PAR (optional, whole number &gt;= 0; leave blank for Not Configured)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={addTemplatePar}
                    onChange={e => setAddTemplatePar(e.target.value)}
                    className="h-11 mt-1 max-w-[180px]"
                    placeholder="Not Configured"
                    data-testid="add-template-par-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddTemplateOpen(false)} disabled={addTemplateBusy}>Cancel</Button>
                <Button onClick={submitAddTemplate} disabled={!addTemplatePick || addTemplateBusy} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="add-template-confirm">
                  {addTemplateBusy ? "Adding…" : "Add"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Remove template row — scope preview + confirm */}
          <Dialog open={Boolean(removeTemplateRow)} onOpenChange={open => { if (!open) { setRemoveTemplateRow(null); setRemoveTemplatePreview(null); } }}>
            <DialogContent data-testid="remove-template-dialog">
              <DialogHeader><DialogTitle>Remove linen item from this room-type template</DialogTitle></DialogHeader>
              {!removeTemplatePreview ? (
                <div className="text-sm text-slate-600 py-3">Loading scope…</div>
              ) : (
                <div className="space-y-3 text-sm">
                  <div className="bg-slate-50 border border-slate-200 rounded p-3">
                    <div className="font-semibold text-slate-900">
                      {removeTemplatePreview.item_name || removeTemplateRow?.legacy_label || "(unmapped row)"}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Rooms of this type: {removeTemplatePreview.rooms_of_this_type}
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800 mb-1">Will be removed</div>
                    <ul className="list-disc list-inside text-slate-700 space-y-1">
                      <li>1 template row ({removeTemplatePreview.is_duplicate ? "duplicate — the retained row stays" : "the linen item will stop appearing in this room-type template"})</li>
                      <li>
                        {removeTemplatePreview.par_rows_to_remove > 0
                          ? `${removeTemplatePreview.par_rows_to_remove} room-type default PAR row(s)`
                          : removeTemplatePreview.is_duplicate
                            ? "0 room-type default PAR rows (shared with the retained duplicate — preserved)"
                            : "0 room-type default PAR rows"}
                      </li>
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-emerald-800 mb-1">Preserved</div>
                    <ul className="list-disc list-inside text-emerald-800 space-y-1">
                      <li>Linen Items Master record (never deleted)</li>
                      <li>Physical stock, ledger history, purchases, dispatches, receipts, closing history</li>
                      <li>{removeTemplatePreview.overrides_preserved} individual room PAR override(s) for this item (editable in Room Linen Par tab)</li>
                    </ul>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => { setRemoveTemplateRow(null); setRemoveTemplatePreview(null); }} disabled={removeTemplateBusy}>Cancel</Button>
                <Button
                  onClick={confirmRemoveTemplate}
                  disabled={!removeTemplatePreview || removeTemplateBusy}
                  className="bg-red-600 text-white hover:bg-red-700"
                  data-testid="remove-template-confirm"
                >
                  {removeTemplateBusy ? "Removing…" : "Remove from template"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="roompar">
          <RoomLinenParPanel />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="migration-reports">
            <MigrationReportsPanel />
          </TabsContent>
        )}

        <TabsContent value="vendors">
          <VendorMaster vendors={assetVendors} onChanged={loadAll} canDelete={isAdmin} />
        </TabsContent>

        <TabsContent value="opening">
          {openingStatus.finalized ? (
            <Card className="p-6 border-emerald-200 bg-emerald-50">
              <div className="font-semibold text-emerald-900">
                {openingStatus.baseline_active
                  ? "Baseline opening allocation finalized."
                  : "Opening allocation finalized."}
              </div>
              <div className="text-sm text-emerald-800 mt-1">Use audited adjustments to change stock now.</div>
            </Card>
          ) : (
            <>
              {/* Stepper */}
              <Card className="p-4 mb-3">
                <div className="flex items-center gap-4 flex-wrap">
                  {[
                    { n: 1, label: "Prepare & pick source" },
                    { n: 2, label: "Distribute lines" },
                    { n: 3, label: "Review & finalize" },
                  ].map((s, i, arr) => (
                    <React.Fragment key={s.n}>
                      <div className={`flex items-center gap-2 ${wizardStep === s.n ? "text-slate-900 font-semibold" : "text-slate-500"}`} data-testid={`wizard-step-${s.n}-label`}>
                        <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${wizardStep === s.n ? "bg-slate-900 text-white" : wizardStep > s.n ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-600"}`}>
                          {wizardStep > s.n ? <CheckCircle2 className="w-4 h-4" /> : s.n}
                        </div>
                        <span className="text-sm">{s.label}</span>
                      </div>
                      {i < arr.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300" />}
                    </React.Fragment>
                  ))}
                </div>
              </Card>

              {wizardStep === 1 && (
                <Card className="p-4">
                  <div className="text-sm text-slate-700 mb-3">
                    <div className="font-semibold text-slate-900 mb-1">Step 1 — Prepare the source</div>
                    {openingStatus.baseline_active ? (
                      <span data-testid="baseline-opening-allocation-note">
                        Record each physical opening count at its actual location. Counted lines do not
                        create purchases, and each item/location can be recorded only once.
                      </span>
                    ) : (
                      <span>
                        Opening stock currently sits in <span className="font-mono">Store Clean</span>
                        for each item. This wizard distributes it to rooms, dirty holding, or an external
                        laundry vendor. The remainder stays in Store Clean.
                      </span>
                    )}
                  </div>
                  {items.filter(i => i.active !== false).length === 0 ? (
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="wizard-no-items">
                      There are no active items yet. Go to the <span className="font-semibold">Items</span> tab and add items (or use Bulk Import) before running this wizard.
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card className="p-3">
                          <div className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wider">Current Store Clean Balances</div>
                          <div className="max-h-52 overflow-y-auto text-sm">
                            {masterRows.filter(r => (r.STORE_CLEAN || 0) > 0).map(r => (
                              <div key={r.item_id} className="border-b border-slate-100 py-1 flex justify-between"><span>{r.name}</span><span className="font-mono">{r.STORE_CLEAN}</span></div>
                            ))}
                            {masterRows.filter(r => (r.STORE_CLEAN || 0) > 0).length === 0 && <div className="text-slate-500">No store balances yet.</div>}
                          </div>
                        </Card>
                        <Card className="p-3">
                          <div className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wider">Auto-suggest from par</div>
                          <p className="text-xs text-slate-600 mb-2">Pick a room type below. The wizard will place <span className="font-semibold">par × active rooms</span> for each item into Room Clean and let you tweak the rest.</p>
                          <Label className="text-xs">Room type</Label>
                          <Select value={wizardRT} onValueChange={setWizardRT}>
                            <SelectTrigger className="h-10 mt-1" data-testid="wizard-room-type"><SelectValue placeholder="Pick a room type" /></SelectTrigger>
                            <SelectContent>{roomTypes.filter(rt => rt.active !== false).map(rt => <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>)}</SelectContent>
                          </Select>
                          <Button className="w-full mt-3 gap-2 bg-slate-900 text-white hover:bg-slate-800" onClick={autoSuggestAllocation} disabled={!wizardRT} data-testid="wizard-auto-suggest">
                            <Wand2 className="w-4 h-4" /> Auto-suggest &amp; go to step 2
                          </Button>
                          <div className="text-xs text-slate-500 mt-2">Or skip auto-suggest and add lines manually.</div>
                          <Button variant="outline" className="w-full mt-1" onClick={() => { addAllocLine(); setWizardStep(2); }} data-testid="wizard-manual">Skip → manual entry</Button>
                        </Card>
                      </div>
                    </>
                  )}
                </Card>
              )}

              {wizardStep === 2 && (
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-semibold text-slate-900">Step 2 — Distribute lines</div>
                      <div className="text-sm text-slate-600">{alloc.length} line(s) queued · total qty {wizardTotalQty}.</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setAlloc([])} data-testid="wizard-clear-lines">Clear all</Button>
                  </div>
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                    {alloc.map((l, i) => (
                      <div key={`line-${i}-${l.item_id}-${l.room_id || l.vendor_id || 'x'}`} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-4"><Select value={l.item_id} onValueChange={v => setAlloc(alloc.map((x, y) => y === i ? { ...x, item_id: v } : x))}><SelectTrigger className="h-11" data-testid={`alloc-item-${i}`}><SelectValue placeholder="Item" /></SelectTrigger><SelectContent>{items.filter(it => it.active !== false).map(it => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</SelectContent></Select></div>
                        <div className="col-span-3">
                          <Select
                            value={l.location}
                            onValueChange={v => setAlloc(alloc.map((x, y) => (
                              y === i ? { ...x, location: v } : x
                            )))}
                          >
                            <SelectTrigger
                              className="h-11"
                              data-testid={`alloc-location-${i}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {openingStatus.baseline_active && (
                                <SelectItem value="STORE_CLEAN">Store Clean</SelectItem>
                              )}
                              <SelectItem value="ROOM_CLEAN">Room Clean/In Use</SelectItem>
                              <SelectItem value="DIRTY_HOLDING">Dirty Holding</SelectItem>
                              <SelectItem value="EXTERNAL_LAUNDRY">At External Laundry</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {l.location === "ROOM_CLEAN" && <div className="col-span-2"><Select value={l.room_id} onValueChange={v => setAlloc(alloc.map((x, y) => y === i ? { ...x, room_id: v } : x))}><SelectTrigger className="h-11"><SelectValue placeholder="Room" /></SelectTrigger><SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.number}</SelectItem>)}</SelectContent></Select></div>}
                        {l.location === "EXTERNAL_LAUNDRY" && <div className="col-span-2"><Select value={l.vendor_id} onValueChange={v => setAlloc(alloc.map((x, y) => y === i ? { ...x, vendor_id: v } : x))}><SelectTrigger className="h-11"><SelectValue placeholder="Vendor" /></SelectTrigger><SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></div>}
                        {l.location !== "ROOM_CLEAN" && l.location !== "EXTERNAL_LAUNDRY" && <div className="col-span-2" />}
                        <div className="col-span-2"><Input type="number" min="1" placeholder="Qty" value={l.qty} onChange={e => setAlloc(alloc.map((x, y) => y === i ? { ...x, qty: e.target.value } : x))} className="h-11" data-testid={`alloc-qty-${i}`} /></div>
                        <div className="col-span-1"><Button variant="ghost" size="icon" onClick={() => setAlloc(alloc.filter((_, y) => y !== i))} data-testid={`alloc-remove-${i}`}><Trash2 className="w-4 h-4 text-red-600" /></Button></div>
                      </div>
                    ))}
                    <Button variant="outline" onClick={addAllocLine} data-testid="alloc-add-line"><Plus className="w-4 h-4 mr-1" /> Add allocation line</Button>
                  </div>
                  <div className="mt-4 flex justify-between">
                    <Button variant="outline" className="gap-2" onClick={() => setWizardStep(1)} data-testid="wizard-back-1"><ChevronLeft className="w-4 h-4" /> Back</Button>
                    <Button className="bg-slate-900 text-white hover:bg-slate-800 gap-2" disabled={wizardLinesValid === 0} onClick={() => setWizardStep(3)} data-testid="wizard-to-3">Next: Review <ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </Card>
              )}

              {wizardStep === 3 && (
                <Card className="p-4">
                  <div className="font-semibold text-slate-900 mb-2">Step 3 — Review &amp; finalize</div>
                  <div className="text-sm text-slate-600 mb-3">
                    {openingStatus.baseline_active
                      ? "Save records these counted balances without a purchase. Finalize locks future baseline counts."
                      : "Save Allocation applies the moves; Finalize applies them and locks opening so future changes require audited adjustments."}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="vr-table">
                      <thead><tr><th>Item</th><th>Destination</th><th>Room / Vendor</th><th className="num">Qty</th></tr></thead>
                      <tbody>
                        {alloc.filter(l => l.item_id && Number(l.qty) > 0).map((l, i) => {
                          const it = items.find(x => x.id === l.item_id);
                          const room = rooms.find(r => r.id === l.room_id);
                          const ven = vendors.find(v => v.id === l.vendor_id);
                          const dest = l.location === "ROOM_CLEAN" ? "Room" : l.location === "DIRTY_HOLDING" ? "Dirty Holding" : "External Laundry";
                          return <tr key={`review-${i}`}><td>{it?.name || l.item_id}</td><td>{dest}</td><td>{room?.number || ven?.name || "—"}</td><td className="num">{l.qty}</td></tr>;
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4 flex justify-between">
                    <Button variant="outline" className="gap-2" onClick={() => setWizardStep(2)} data-testid="wizard-back-2"><ChevronLeft className="w-4 h-4" /> Back to edit</Button>
                    <div className="flex gap-2">
                      <Button
                        className="bg-slate-900 text-white hover:bg-slate-800"
                        onClick={() => submitAlloc(false)}
                        disabled={allocationBusy}
                        data-testid="alloc-save"
                      >
                        Save Allocation
                      </Button>
                      <Button
                        className="bg-amber-500 text-white hover:bg-amber-600"
                        onClick={() => submitAlloc(true)}
                        disabled={allocationBusy}
                        data-testid="alloc-finalize"
                      >
                        Save &amp; Finalize (Lock)
                      </Button>
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <DialogContent className="max-w-md" data-testid="clone-par-dialog">
          <DialogHeader><DialogTitle>Clone Par Quantities</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 mb-3">Copy all par values from a source room type into <span className="font-semibold">{roomTypes.find(rt => rt.id === selectedRT)?.name || "current type"}</span>. This replaces the current pars.</div>
          <div>
            <Label>Source Room Type</Label>
            <Select value={cloneSourceId} onValueChange={setCloneSourceId}>
              <SelectTrigger className="h-11 mt-1" data-testid="clone-par-source"><SelectValue placeholder="Pick source" /></SelectTrigger>
              <SelectContent>{roomTypes.filter(rt => rt.id !== selectedRT).map(rt => <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={clonePars} data-testid="clone-par-confirm">Clone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Item Import */}
      <Dialog open={bulkOpen} onOpenChange={(v) => { setBulkOpen(v); if (!v) { setBulkCsv(""); setBulkPreview([]); setBulkMappingMode(""); } }}>
        <DialogContent className="max-w-3xl" data-testid="bulk-import-dialog">
          <DialogHeader><DialogTitle>Bulk import linen items (CSV)</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 mb-2">
            Paste a comma-separated list. Columns: <span className="font-mono text-slate-800">name, category, size, unit, opening_qty, low_stock_threshold, par_min</span> (only <span className="font-semibold">name</span> is required). A header row is optional.
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label className="text-xs">Linen use for this import</Label>
              <Select value={bulkMappingMode} onValueChange={setBulkMappingMode}>
                <SelectTrigger className="mt-1 h-11" data-testid="bulk-mapping-mode">
                  <SelectValue placeholder="Choose mapping" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="store_only">Store-only linen — No room mapping required</SelectItem>
                </SelectContent>
              </Select>
              <div className="mt-1 text-xs text-slate-500">
                Use individual item creation for room linen and its room-type PAR values.
              </div>
            </div>
            <div>
              <Label className="text-xs">CSV content</Label>
              <Textarea
                value={bulkCsv}
                onChange={e => setBulkCsv(e.target.value)}
                className="font-mono text-xs h-40 mt-1"
                placeholder={"name,category,size,unit,opening_qty,low_stock_threshold,par_min\nKing Bed Sheet,Bed Linen,King,pcs,100,20,2\nBath Towel,Towels,,pcs,300,60,4"}
                data-testid="bulk-csv-textarea"
              />
              <div className="flex justify-between mt-2">
                <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <Upload className="w-3.5 h-3.5" /> Or upload a .csv file
                  <input type="file" accept=".csv,text/csv" className="hidden" data-testid="bulk-csv-file"
                    onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      const txt = await f.text(); setBulkCsv(txt); e.target.value = "";
                    }} />
                </label>
                <Button variant="outline" size="sm" onClick={previewCsv} data-testid="bulk-preview">Parse &amp; preview</Button>
              </div>
            </div>
            {bulkPreview.length > 0 && (
              <div className="border border-slate-200 rounded max-h-64 overflow-y-auto">
                <table className="vr-table">
                  <thead><tr><th>#</th><th>Name</th><th>Unit</th><th className="num">Opening</th><th className="num">Low</th><th className="num">Par min</th></tr></thead>
                  <tbody>
                    {bulkPreview.map((r, i) => (
                      <tr key={`preview-${r._row}-${r.name}`}>
                        <td className="text-xs text-slate-500">{i + 1}</td>
                        <td>{r.name}</td><td>{r.unit}</td>
                        <td className="num">{r.opening_qty}</td><td className="num">{r.low_stock_threshold}</td><td className="num">{r.par_min}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={importCsv} disabled={bulkBusy || !bulkPreview.length} data-testid="bulk-import-submit">
              {bulkBusy ? "Importing…" : `Import ${bulkPreview.length || ""} items`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
